import { ChangeEvent, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  CustomButton,
  SmallBackButton,
} from "../../../../core/components/CustomButton";
import { CustomInput } from "../../../../core/components/CustomInput";
import { TextComponent } from "../../../../core/components/TextComponent";
import { AppDispatch } from "../../../../core/store/store";
import { onSetPassword } from "../../../auth/presentation/store/auth/AuthThunk";

interface ChangePasswordScreenProps {
  onBack?: () => void;
}

export const ChangePasswordScreen = ({
  onBack,
}: ChangePasswordScreenProps) => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    if (password.length < 8) {
      setSuccessMessage("");
      setErrorMessage("Please use at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setSuccessMessage("");
      setErrorMessage("Please make sure both password boxes match.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    const response = await dispatch(onSetPassword({ password }));

    setIsSubmitting(false);

    if (onSetPassword.fulfilled.match(response)) {
      setPassword("");
      setConfirmPassword("");
      setSuccessMessage(
        "Your password has been updated. You can use it the next time you sign in.",
      );
      return;
    }

    const message = (response as any).error?.message ?? "";
    if (message === "401" || message.toLowerCase().includes("unauthorized")) {
      setErrorMessage("Your session has expired. Please log in again.");
      return;
    }
    setErrorMessage(
      message || "Could not update your password right now. Please try again.",
    );
  };

  return (
    <div className="bg-white p-6 md:p-8">
      <SmallBackButton
        onClick={() => (onBack ? onBack() : navigate("/settings"))}
        className="mb-4"
      />
      <div className="max-w-xl">
        <TextComponent variant="h2" className="font-bold">
          Change password
        </TextComponent>
        <TextComponent variant="h6" className="mt-3 text-gray-500">
          Set a new password for your account.
        </TextComponent>
        <div className="mt-5">
          <CustomInput
            label="New Password"
            hintText="Enter new password"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            obscureText
          />
        </div>
        <div className="mt-4">
          <CustomInput
            label="Confirm New Password"
            hintText="Repeat new password"
            value={confirmPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(e.target.value)
            }
            obscureText
          />
        </div>
        {errorMessage && (
          <div className="mt-3 text-red-500">{errorMessage}</div>
        )}
        {successMessage && (
          <div className="mt-3 text-green-600">{successMessage}</div>
        )}
        <CustomButton
          type="button"
          className="mt-9"
          onClick={onSubmit}
          loading={isSubmitting}
        >
          UPDATE PASSWORD
        </CustomButton>
      </div>
    </div>
  );
};
